import React, { useEffect, useState, useCallback } from 'react';
import { expensasPortalApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Receipt, Upload, FileText, Clock, CheckCircle2, XCircle, AlertTriangle, Wallet, ShieldCheck, AlertOctagon, Download } from 'lucide-react';

const fmtMoney = (n) => {
  const v = Math.round(Number(n || 0));
  return `$ ${v.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
};
const fmtFecha = (s) => {
  if (!s) return '—';
  const d = String(s).slice(0, 10).split('-');
  return d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : s;
};

// Estados de cobro → etiqueta Pagado / Parcial / Pendiente (sin "Vigente").
const ESTADO_COBRO = {
  pendiente: { label: 'Pendiente', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  parcial: { label: 'Parcial', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  pagado: { label: 'Pagado', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pagado_tarde: { label: 'Pagado', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  vencido: { label: 'Pendiente', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  en_revision: { label: 'En revisión', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
};
const ESTADO_INFORME = {
  en_revision: { label: 'En revisión', cls: 'bg-violet-50 text-violet-700 border-violet-200', icon: Clock },
  aprobado: { label: 'Aprobado', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  rechazado: { label: 'Rechazado', cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle },
  correccion: { label: 'Requiere corrección', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle },
};
const MEDIOS = [
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'deposito', label: 'Depósito' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'mercado_pago', label: 'Mercado Pago' },
  { value: 'otro', label: 'Otro' },
];
const MEDIO_LABEL = MEDIOS.reduce((a, m) => ({ ...a, [m.value]: m.label }), {});

const hoyISO = () => new Date().toISOString().slice(0, 10);

const EstadoBadge = ({ estado }) => {
  const cfg = ESTADO_COBRO[estado] || ESTADO_COBRO.pendiente;
  return <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full border ${cfg.cls}`}>{cfg.label}</span>;
};

const useEstadoCuenta = (unidadId) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fetchData = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await expensasPortalApi.estadoCuenta(unidadId);
      setRows(r.data || []);
    } catch {
      setError(true);
    } finally { setLoading(false); }
  }, [unidadId]);
  useEffect(() => { fetchData(); }, [fetchData]);
  return { rows, loading, error, refetch: fetchData };
};

// Descarga de comprobante (abre en nueva pestaña).
const ComprobanteLink = ({ informeId, className }) => (
  <a href={expensasPortalApi.comprobanteUrl(informeId)} target="_blank" rel="noreferrer"
    className={className || 'text-xs text-indigo-600 inline-flex items-center gap-1'}>
    <FileText className="w-3 h-3" /> Comprobante
  </a>
);

// ══════════════════════════════ SOLAPA: PAGOS ══════════════════════════════
export const ExpensasEstadoCuenta = ({ unidadId }) => {
  const { rows, loading, error, refetch } = useEstadoCuenta(unidadId);
  const [formRow, setFormRow] = useState(null);
  const hayRecargo = rows.some((r) => Number(r.recargo || 0) > 0);

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (error) return <div className="py-12 text-center text-slate-500">No se pudo cargar el historial de pagos.</div>;
  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-8 text-center" data-testid="pagos-vacio">
        <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
        <p className="text-slate-700 font-medium">Sin movimientos de expensas</p>
        <p className="text-sm text-slate-500 mt-1">Cuando haya liquidaciones aprobadas para esta unidad vas a verlas acá.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="pagos-tab">
      {/* Desktop */}
      <div className="hidden md:block bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium">Período</th>
              <th className="text-right px-4 py-2.5 font-medium">Liquidado</th>
              {hayRecargo && <th className="text-right px-4 py-2.5 font-medium">Intereses/recargo</th>}
              <th className="text-right px-4 py-2.5 font-medium">Total actualizado</th>
              <th className="text-right px-4 py-2.5 font-medium">Pagado</th>
              <th className="text-right px-4 py-2.5 font-medium">Saldo</th>
              <th className="text-center px-4 py-2.5 font-medium">Estado</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <React.Fragment key={r.periodo_id}>
                <tr data-testid={`ec-row-${r.periodo}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{r.periodo_label}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{fmtMoney(r.monto_liquidado)}</td>
                  {hayRecargo && (
                    <td className="px-4 py-3 text-right text-amber-700">{Number(r.recargo || 0) > 0 ? fmtMoney(r.recargo) : '—'}</td>
                  )}
                  <td className="px-4 py-3 text-right text-slate-800 font-medium">{fmtMoney(r.total_actualizado)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700">{fmtMoney(r.pagado)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmtMoney(r.saldo)}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <EstadoBadge estado={r.estado} />
                      {r.tiene_pago_en_revision && (
                        <span className="text-[10px] text-violet-600 flex items-center gap-0.5"><Clock className="w-3 h-3" /> pago en revisión</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {Number(r.saldo || 0) > 0 && (
                      <Button size="sm" variant="outline" disabled={r.tiene_pago_en_revision}
                        onClick={() => setFormRow(r)} data-testid={`informar-pago-${r.periodo}`}>
                        <Wallet className="w-3.5 h-3.5 mr-1" /> Informar pago
                      </Button>
                    )}
                  </td>
                </tr>
                {(r.pagos || []).length > 0 && (
                  <tr className="bg-slate-50/60">
                    <td colSpan={hayRecargo ? 8 : 7} className="px-4 py-2">
                      <div className="space-y-1" data-testid={`ec-pagos-${r.periodo}`}>
                        {r.pagos.map((p, i) => (
                          <div key={i} className="flex items-center justify-between text-xs text-slate-600">
                            <span className="flex items-center gap-2">
                              <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                              Pago {fmtFecha(p.fecha_pago)} · {MEDIO_LABEL[p.metodo_pago] || p.metodo_pago || 'Pago'}
                              {p.componente ? ` · ${p.componente}` : ''}
                            </span>
                            <span className="flex items-center gap-3">
                              <span className="font-mono text-slate-700">{fmtMoney(p.monto)}</span>
                              {p.tiene_comprobante && p.origen_informe_id && (
                                <ComprobanteLink informeId={p.origen_informe_id} />
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {rows.map((r) => (
          <div key={r.periodo_id} className="bg-white rounded-lg border border-slate-200 p-3" data-testid={`ec-card-${r.periodo}`}>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-800">{r.periodo_label}</span>
              <EstadoBadge estado={r.estado} />
            </div>
            <div className="grid grid-cols-2 gap-1 mt-2 text-xs text-slate-600">
              <div>Liquidado: <b>{fmtMoney(r.monto_liquidado)}</b></div>
              {Number(r.recargo || 0) > 0 && <div>Recargo: <b className="text-amber-700">{fmtMoney(r.recargo)}</b></div>}
              <div>Total actualizado: <b>{fmtMoney(r.total_actualizado)}</b></div>
              <div>Pagado: <b className="text-emerald-700">{fmtMoney(r.pagado)}</b></div>
              <div>Saldo: <b>{fmtMoney(r.saldo)}</b></div>
            </div>
            {(r.pagos || []).length > 0 && (
              <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                {r.pagos.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px] text-slate-500">
                    <span>{fmtFecha(p.fecha_pago)} · {MEDIO_LABEL[p.metodo_pago] || p.metodo_pago}</span>
                    <span className="flex items-center gap-2">
                      <span className="font-mono">{fmtMoney(p.monto)}</span>
                      {p.tiene_comprobante && p.origen_informe_id && <ComprobanteLink informeId={p.origen_informe_id} />}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {Number(r.saldo || 0) > 0 && (
              <Button size="sm" variant="outline" className="w-full mt-2" disabled={r.tiene_pago_en_revision}
                onClick={() => setFormRow(r)} data-testid={`informar-pago-m-${r.periodo}`}>
                <Wallet className="w-3.5 h-3.5 mr-1" /> Informar pago
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Historial de pagos informados (en revisión / rechazados / corrección) */}
      {rows.some((r) => (r.pagos_informados || []).some((p) => p.estado !== 'aprobado')) && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h4 className="text-sm font-semibold text-slate-800 mb-2">Pagos informados</h4>
          <div className="space-y-2">
            {rows.flatMap((r) => (r.pagos_informados || []).filter((p) => p.estado !== 'aprobado').map((p) => ({ ...p, periodo_label: r.periodo_label })))
              .map((p) => {
                const cfg = ESTADO_INFORME[p.estado] || ESTADO_INFORME.en_revision;
                const Icon = cfg.icon;
                return (
                  <div key={p.pago_informado_id} className="flex items-start justify-between gap-2 text-sm border-b border-slate-50 pb-2 last:border-0" data-testid={`informe-${p.pago_informado_id}`}>
                    <div>
                      <div className="text-slate-800">{p.periodo_label} · {fmtMoney(p.monto)} · {fmtFecha(p.fecha_pago)}</div>
                      <div className="text-xs text-slate-500">{p.metodo_pago}{p.comentario ? ` · ${p.comentario}` : ''}</div>
                      {p.revision?.motivo && p.estado !== 'aprobado' && (
                        <div className="text-xs text-rose-600 mt-0.5">Motivo: {p.revision.motivo}</div>
                      )}
                      {p.comprobante && <ComprobanteLink informeId={p.pago_informado_id} className="text-xs text-indigo-600 inline-flex items-center gap-1 mt-0.5" />}
                    </div>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${cfg.cls} flex-shrink-0`}>
                      <Icon className="w-3 h-3" /> {cfg.label}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {formRow && (
        <InformarPagoDialog unidadId={unidadId} row={formRow} onClose={() => setFormRow(null)} onDone={() => { setFormRow(null); refetch(); }} />
      )}
    </div>
  );
};

// ═══════════════════════ SOLAPA: ESTADO DE CUENTA ═══════════════════════
export const ExpensasResumenDeuda = ({ unidadId }) => {
  const { rows, loading, error } = useEstadoCuenta(unidadId);
  const [descargando, setDescargando] = useState(false);

  const descargarPDF = async () => {
    setDescargando(true);
    try {
      const res = await expensasPortalApi.constanciaDeuda(unidadId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'constancia_expensas.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo generar el PDF. Intentá nuevamente.');
    } finally { setDescargando(false); }
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (error) return <div className="py-12 text-center text-slate-500">No se pudo cargar el estado de cuenta.</div>;

  const conDeuda = rows.filter((r) => Number(r.saldo || 0) > 0);
  const totalDeuda = conDeuda.reduce((s, r) => s + Number(r.saldo || 0), 0);
  const tieneDeuda = totalDeuda > 0;

  return (
    <div className="space-y-4" data-testid="estado-cuenta-tab">
      {/* Encabezado de estado */}
      <div className={`rounded-lg border p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${tieneDeuda ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`}>
        <div className="flex items-start gap-3">
          {tieneDeuda ? <AlertOctagon className="w-8 h-8 text-rose-600 flex-shrink-0" /> : <ShieldCheck className="w-8 h-8 text-emerald-600 flex-shrink-0" />}
          <div>
            {tieneDeuda ? (
              <>
                <p className="text-lg font-bold text-rose-800" data-testid="estado-deuda-titulo">Deuda pendiente</p>
                <p className="text-sm text-rose-700 mt-0.5">Total adeudado a la fecha</p>
                <p className="text-2xl font-bold font-mono text-rose-900 mt-1" data-testid="estado-deuda-total">{fmtMoney(totalDeuda)}</p>
              </>
            ) : (
              <>
                <p className="text-lg font-bold text-emerald-800" data-testid="estado-sin-deuda-titulo">Sin deuda al día de hoy</p>
                <p className="text-sm text-emerald-700 mt-0.5">Esta unidad no registra saldos pendientes de expensas.</p>
              </>
            )}
          </div>
        </div>
        <Button onClick={descargarPDF} disabled={descargando}
          className={tieneDeuda ? 'bg-rose-700 hover:bg-rose-800' : 'bg-emerald-700 hover:bg-emerald-800'}
          data-testid="descargar-constancia-pdf">
          {descargando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
          {tieneDeuda ? 'PDF Estado de deuda' : 'PDF Libre de deuda'}
        </Button>
      </div>

      {/* Detalle por período (solo si hay deuda) */}
      {tieneDeuda && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800">Detalle por período</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Período</th>
                  <th className="text-right px-4 py-2.5 font-medium">Capital</th>
                  <th className="text-right px-4 py-2.5 font-medium">Intereses/recargo</th>
                  <th className="text-right px-4 py-2.5 font-medium">Pagado</th>
                  <th className="text-right px-4 py-2.5 font-medium">Saldo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {conDeuda.map((r) => (
                  <tr key={r.periodo_id} data-testid={`deuda-row-${r.periodo}`}>
                    <td className="px-4 py-2.5 font-medium text-slate-800">{r.periodo_label}</td>
                    <td className="px-4 py-2.5 text-right text-slate-700">{fmtMoney(r.monto_liquidado)}</td>
                    <td className="px-4 py-2.5 text-right text-amber-700">{Number(r.recargo || 0) > 0 ? fmtMoney(r.recargo) : '—'}</td>
                    <td className="px-4 py-2.5 text-right text-emerald-700">{fmtMoney(r.pagado)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-rose-800">{fmtMoney(r.saldo)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t border-slate-200">
                  <td className="px-4 py-2.5 font-semibold text-slate-800" colSpan={4}>Total adeudado</td>
                  <td className="px-4 py-2.5 text-right font-bold font-mono text-rose-900">{fmtMoney(totalDeuda)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const InformarPagoDialog = ({ unidadId, row, onClose, onDone }) => {
  const [monto, setMonto] = useState(String(row.saldo || ''));
  const [fecha, setFecha] = useState(hoyISO());
  const [medio, setMedio] = useState('transferencia');
  const [comentario, setComentario] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const m = Math.round(Number(monto) || 0);
    if (m <= 0) return toast.error('Ingresá un monto válido');
    if (!fecha) return toast.error('Ingresá la fecha de pago');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('periodo', row.periodo);
      fd.append('monto', String(m));
      fd.append('fecha_pago', fecha);
      fd.append('metodo_pago', medio);
      if (comentario.trim()) fd.append('comentario', comentario.trim());
      if (file) fd.append('comprobante', file);
      await expensasPortalApi.informarPago(unidadId, fd);
      toast.success('Pago informado. Queda en revisión por administración.');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo informar el pago');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="informar-pago-dialog">
        <DialogHeader>
          <DialogTitle>Informar pago — {row.periodo_label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-500 bg-slate-50 rounded p-2">
            Saldo del período: <b className="text-slate-800">{fmtMoney(row.saldo)}</b>. El pago quedará <b>en revisión</b> hasta que administración lo valide.
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Fecha real de pago</label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="mt-1" data-testid="ip-fecha" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Monto pagado</label>
            <Input type="number" value={monto} onChange={(e) => setMonto(e.target.value)} className="mt-1" data-testid="ip-monto" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Medio de pago</label>
            <Select value={medio} onValueChange={setMedio}>
              <SelectTrigger className="mt-1" data-testid="ip-medio"><SelectValue /></SelectTrigger>
              <SelectContent>{MEDIOS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Comprobante (imagen o PDF)</label>
            <label className="mt-1 flex items-center gap-2 border border-dashed border-slate-300 rounded px-3 py-2 cursor-pointer hover:bg-slate-50">
              <Upload className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-600 truncate">{file ? file.name : 'Seleccionar archivo'}</span>
              <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ip-file" />
            </label>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Comentario (opcional)</label>
            <Textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={2} className="mt-1" data-testid="ip-comentario" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#1B4D3E] hover:bg-[#163f33]" data-testid="ip-submit">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Informar pago
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExpensasEstadoCuenta;
